import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ArrowLeft, Layers, AlertTriangle, Info, Flame, GitCompareArrows } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { rpc, RpcError } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ToolHeader } from '@/components/common/ToolHeader';
import { SessionSelector } from '@/components/sector/SessionSelector';
import { WizardStepper } from '@/components/sector/WizardStepper';
import { SessionRemovedAnalysisDialog } from '@/components/analysis/SessionRemovedAnalysisDialog';
import { useWizardState } from '@/hooks/useWizardState';
import { formatTime } from '@/lib/format';
import type {
  SessionSummary,
  ListSessionsResponse,
  ListAnalysisTracksResponse,
  SessionDetailResponse,
  AnalyzeAggregationResponse,
  AggregationLapResult,
  AnalyzeResponse,
  GetAnalysisResponse,
  AnalysisRecord,
  AggregationSegment,
  AggregationPoint,
} from '@/types/telemetry';
import type { TrackShape, TrackAnnotation, TrackPoint, SplineSample } from '@/lib/spline';
import { sampleClosedSpline, computeRibbonBoundaries, findClosestParametricT, haversineDistanceM } from '@/lib/spline';
import { InteractiveTrackMap } from '@/components/common/trackmap/InteractiveTrackMap';
import type { OverlayUtils } from '@/components/common/trackmap/InteractiveTrackMap';
import { projectToLocalMeters } from '@/lib/map/mapGeometry';
import type { GeoCoord } from '@/lib/map/mapGeometry';
import { drawGpsLineHeatmap, drawSelectedLapGpsLine } from '@/lib/map/gpsHeatmap';

function getSplineSampleAtDistanceM(
  targetDistM: number,
  samples: SplineSample[],
  totalLengthM: number
): { point: TrackPoint; normal: { x: number; y: number }; widthM: number } {
  const n = samples.length;
  if (n === 0) return { point: { lat: 0, lng: 0 }, normal: { x: 0, y: 1 }, widthM: 4.0 };
  if (totalLengthM <= 0) totalLengthM = 1.0;
  const d = ((targetDistM % totalLengthM) + totalLengthM) % totalLengthM;

  let low = 0, high = n - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (samples[mid].distanceM <= d) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  const i0 = Math.max(0, Math.min(n - 1, high));
  const i1 = (i0 + 1) % n;
  const s0 = samples[i0];
  const s1 = samples[i1];

  let d0 = s0.distanceM;
  let d1 = s1.distanceM;
  if (i1 === 0) {
    d1 = totalLengthM;
  }
  const span = d1 - d0;
  const u = span > 1e-6 ? Math.max(0, Math.min(1, (d - d0) / span)) : 0;

  const lat = s0.point.lat + u * (s1.point.lat - s0.point.lat);
  const lng = s0.point.lng + u * (s1.point.lng - s0.point.lng);
  const nx = s0.normal.x + u * (s1.normal.x - s0.normal.x);
  const ny = s0.normal.y + u * (s1.normal.y - s0.normal.y);
  const nLen = Math.sqrt(nx * nx + ny * ny) || 1;
  const widthM = s0.widthM + u * (s1.widthM - s0.widthM);

  return {
    point: { lat, lng },
    normal: { x: nx / nLen, y: ny / nLen },
    widthM,
  };
}

function getOffsetPoint(sample: { point: TrackPoint; normal: { x: number; y: number } }, offsetM: number): TrackPoint {
  const latRad = (sample.point.lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad);
  const dLat = ((sample.normal.y * offsetM) / 6371000.0) * (180 / Math.PI);
  const dLon = ((sample.normal.x * offsetM) / (6371000.0 * cosLat)) * (180 / Math.PI);
  return {
    lat: sample.point.lat + dLat,
    lng: sample.point.lng + dLon,
  };
}

function drawDistanceCenterlineArc(
  distStartM: number,
  distEndM: number,
  finishDistM: number,
  totalLengthM: number,
  splineSamples: SplineSample[],
  project: (latRaw: number, lonRaw: number) => { x: number; y: number },
  ctx: CanvasRenderingContext2D,
  strokeColor: string,
  lineWidth: number,
) {
  let spanM = distEndM - distStartM;
  if (spanM < 0) spanM += totalLengthM;
  if (spanM < 0.1) return;

  const numSteps = Math.max(3, Math.ceil(spanM / 2.0));
  ctx.beginPath();
  for (let i = 0; i <= numSteps; i++) {
    const frac = i / numSteps;
    const d = (distStartM + frac * spanM) % totalLengthM;
    const absD = (finishDistM + d) % totalLengthM;
    const s = getSplineSampleAtDistanceM(absD, splineSamples, totalLengthM);
    const cp = project(Math.round(s.point.lat * 1e6), Math.round(s.point.lng * 1e6));
    if (i === 0) ctx.moveTo(cp.x, cp.y);
    else ctx.lineTo(cp.x, cp.y);
  }
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

function drawDistanceBoxPolygon(
  distStartM: number,
  distEndM: number,
  finishDistM: number,
  totalLengthM: number,
  splineSamples: SplineSample[],
  project: (latRaw: number, lonRaw: number) => { x: number; y: number },
  ctx: CanvasRenderingContext2D,
  fillColor: string,
  strokeColor?: string,
  lineWidth?: number,
  halfWidthM?: number,
) {
  let spanM = distEndM - distStartM;
  if (spanM < 0) spanM += totalLengthM;
  if (spanM < 0.1) return;

  const numSteps = Math.max(4, Math.ceil(spanM / 2.0));
  const leftCanvas: Array<{ x: number; y: number }> = [];
  const rightCanvas: Array<{ x: number; y: number }> = [];

  for (let i = 0; i <= numSteps; i++) {
    const frac = i / numSteps;
    const d = (distStartM + frac * spanM) % totalLengthM;
    const absD = (finishDistM + d) % totalLengthM;
    const s = getSplineSampleAtDistanceM(absD, splineSamples, totalLengthM);
    const hw = halfWidthM !== undefined && halfWidthM > 0 ? halfWidthM : (s.widthM || 8.0) / 2.0;
    const pLeft = getOffsetPoint(s, hw);
    const pRight = getOffsetPoint(s, -hw);
    leftCanvas.push(project(Math.round(pLeft.lat * 1e6), Math.round(pLeft.lng * 1e6)));
    rightCanvas.push(project(Math.round(pRight.lat * 1e6), Math.round(pRight.lng * 1e6)));
  }

  ctx.beginPath();
  leftCanvas.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); });
  for (let i = rightCanvas.length - 1; i >= 0; i--) {
    ctx.lineTo(rightCanvas[i].x, rightCanvas[i].y);
  }
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();
  if (strokeColor && lineWidth && lineWidth > 0) {
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }
}

function drawDistancePerpendicularTick(
  distFromFinishM: number,
  finishDistM: number,
  totalLengthM: number,
  splineSamples: SplineSample[],
  project: (latRaw: number, lonRaw: number) => { x: number; y: number },
  ctx: CanvasRenderingContext2D,
  color: string,
  lineWidth: number,
  halfWidthM?: number,
) {
  const absD = ((finishDistM + distFromFinishM) % totalLengthM + totalLengthM) % totalLengthM;
  const s = getSplineSampleAtDistanceM(absD, splineSamples, totalLengthM);
  const hw = halfWidthM !== undefined && halfWidthM > 0 ? halfWidthM : (s.widthM || 8.0) / 2.0;
  const p1 = getOffsetPoint(s, hw);
  const p2 = getOffsetPoint(s, -hw);
  const c1 = project(Math.round(p1.lat * 1e6), Math.round(p1.lng * 1e6));
  const c2 = project(Math.round(p2.lat * 1e6), Math.round(p2.lng * 1e6));

  ctx.beginPath();
  ctx.moveTo(c1.x, c1.y);
  ctx.lineTo(c2.x, c2.y);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

interface AggregationAnalysisProps {
  trackName: string;
  initialSessionId?: string;
}

export function AggregationAnalysis({ trackName, initialSessionId }: AggregationAnalysisProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const wizardSteps = [t('gearshifting.wizardChooseTrack'), t('gearshifting.wizardSelect'), t('gearshifting.wizardResults')];
  const [wizardState, updateWizardState] = useWizardState({
    step: trackName ? (initialSessionId ? 2 : 1) : 0,
    draftTrack: (trackName || undefined) as string | undefined,
    selectedIds: [] as string[],
    driverFilter: 'all',
    weatherFilter: 'all',
    selectedLapKey: '' as string,
    showHeatmap: true,
    showZones: true,
  });

  const {
    step,
    draftTrack,
    selectedIds: selectedIdsArr,
    driverFilter,
    weatherFilter,
    selectedLapKey,
    showHeatmap,
    showZones,
  } = wizardState;
  const selectedIds = new Set(selectedIdsArr);

  const setStep = useCallback((newStep: number | ((prev: number) => number)) => {
    updateWizardState(prev => {
      const nextStep = typeof newStep === 'function' ? newStep(prev.step) : newStep;
      return { step: nextStep };
    }, false);
  }, [updateWizardState]);

  const setDraftTrack = useCallback((track: string | undefined) => updateWizardState({ draftTrack: track }), [updateWizardState]);
  const setSelectedIds = useCallback((ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    updateWizardState(prev => {
      const nextIds = typeof ids === 'function' ? ids(new Set(prev.selectedIds)) : ids;
      return { selectedIds: [...nextIds] };
    });
  }, [updateWizardState]);
  const setDriverFilter = useCallback((filter: string) => updateWizardState({ driverFilter: filter }), [updateWizardState]);
  const setWeatherFilter = useCallback((filter: string) => updateWizardState({ weatherFilter: filter }), [updateWizardState]);
  const setSelectedLapKey = useCallback((key: string) => updateWizardState({ selectedLapKey: key }), [updateWizardState]);
  const setShowHeatmap = useCallback((show: boolean) => updateWizardState({ showHeatmap: show }), [updateWizardState]);
  const setShowZones = useCallback((show: boolean) => updateWizardState({ showZones: show }), [updateWizardState]);

  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [availableTracks, setAvailableTracks] = useState<string[]>([]);
  const [trackId, setTrackId] = useState('');

  const [analysisResult, setAnalysisResult] = useState<AnalyzeAggregationResponse | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<string | undefined>(draftTrack || trackName || undefined);
  const [loading, setLoading] = useState(!trackName);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [sessionRemovedAnalysis, setSessionRemovedAnalysis] = useState<AnalysisRecord | null>(null);

  const [trackShape, setTrackShape] = useState<TrackShape | null>(null);
  const [trackAnnotations, setTrackAnnotations] = useState<TrackAnnotation[]>([]);
  const [hoveredSegment, setHoveredSegment] = useState<{
    segment: AggregationSegment;
    x: number;
    y: number;
  } | null>(null);

  const handleLoadAnalysis = useCallback(async (analysis: AnalysisRecord) => {
    const agg = analysis.aggregation;
    if (agg?.track) {
      const tName = agg.track.trackName || agg.track.trackId;
      setSelectedTrack(tName);
      setTrackId(agg.track.trackId);
    }
    if (agg?.sessions?.sessionIds) {
      setSelectedIds(new Set(agg.sessions.sessionIds));
    }
    setAnalyzing(true);
    try {
      if (analysis.id) {
        const resp = await rpc<GetAnalysisResponse>('GetAnalysis', { id: analysis.id });
        if (resp.aggregation) {
          setAnalysisResult(resp.aggregation);
          setSelectedLapKey('');
          setStep(2);
        }
      } else {
        const resp = await rpc<AnalyzeResponse>('Analyze', {
          aggregation: agg,
          skipSave: true,
        });
        if (resp.aggregation) {
          setAnalysisResult(resp.aggregation);
          setSelectedLapKey('');
          setStep(2);
        }
      }
    } catch (err: any) {
      console.error('Failed to evaluate loaded aggregation analysis:', err);
      const isSessionMissing = err?.status === 404 || err?.isNotFound || (err?.message && (err.message.includes('session') || err.message.includes('not found')));
      if (isSessionMissing) {
        setSessionRemovedAnalysis(analysis);
      } else {
        setError(err?.message || 'Failed to evaluate aggregation analysis');
      }
    } finally {
      setAnalyzing(false);
    }
  }, [setStep, setSelectedIds, setSelectedLapKey]);

  // Handle restoring analysis from history navigation
  useEffect(() => {
    const stored = sessionStorage.getItem('loaded_analysis_record');
    if (stored) {
      sessionStorage.removeItem('loaded_analysis_record');
      try {
        const rec = JSON.parse(stored) as AnalysisRecord;
        if (rec.type === 'ANALYSIS_TYPE_AGGREGATION') {
          handleLoadAnalysis(rec);
        }
      } catch {}
    }
  }, [handleLoadAnalysis]);

  // Top 10 fastest laps
  const top10 = useMemo(() => {
    if (!analysisResult?.laps) return [];
    // Already sorted by lapTimeMs from backend
    return analysisResult.laps.slice(0, 10);
  }, [analysisResult]);

  // Total laps for transparency calculation
  const totalLaps = analysisResult?.laps?.length ?? 0;

  // Group top10 by session for the grouped list
  const sessionGroups = useMemo(() => {
    const groups: Array<{ sessionId: string; date: string; time: string; driverName: string; laps: Array<AggregationLapResult & { rank: number }> }> = [];
    const groupMap = new Map<string, typeof groups[0]>();
    
    top10.forEach((lap, i) => {
      const key = lap.sessionId;
      let group = groupMap.get(key);
      if (!group) {
        group = { sessionId: key, date: lap.sessionDate, time: lap.sessionTime, driverName: lap.driverName, laps: [] };
        groupMap.set(key, group);
        groups.push(group);
      }
      group.laps.push({ ...lap, rank: i + 1 });
    });
    return groups;
  }, [top10]);

  // Draw GPS driving line heat map, box plots, and selected lap highlight on top of track map
  const renderOverlay = useCallback(
    (ctx: CanvasRenderingContext2D, { toCanvas, anchor, viewport, width, height }: OverlayUtils) => {
      if (!analysisResult) return;

      const project = (latRaw: number, lonRaw: number) => {
        const p = projectToLocalMeters({ lat: latRaw / 1e6, lng: lonRaw / 1e6 }, anchor);
        return toCanvas(p);
      };

      const hasShape = !!(trackShape && trackShape.centerline && trackShape.centerline.length >= 3);
      const trackWidthM = trackShape?.trackWidthM || 8.0;
      const splineSamples = hasShape
        ? sampleClosedSpline(trackShape!.centerline, trackWidthM, 30)
        : [];

      // 1. Box Plot Segments along Centerline (drawn underneath the GPS heat map)
      if (showZones && hasShape) {
        const loopClosingDist = splineSamples.length > 0
          ? haversineDistanceM(splineSamples[splineSamples.length - 1].point, splineSamples[0].point)
          : 0;
        const totalLengthM = splineSamples.length > 0
          ? splineSamples[splineSamples.length - 1].distanceM + loopClosingDist
          : (trackShape!.totalLengthM || 1000.0);

        const finishLineAnnot = (trackAnnotations || []).find(a => a.type === 'finish_line');
        let finishDistM = 0.0;
        if (finishLineAnnot?.location && splineSamples.length > 0) {
          const finishClosest = findClosestParametricT(finishLineAnnot.location, splineSamples);
          finishDistM = splineSamples[finishClosest.index]?.distanceM ?? 0.0;
        }

        const segments = analysisResult.segments || [];
        const whiskerAlpha = showHeatmap ? 0.12 : 0.18;
        const medianAlpha = showHeatmap ? 0.26 : 0.45;

        for (const seg of segments) {
          const isBraking = seg.eventType === 'AGGREGATION_EVENT_TYPE_BRAKING' || seg.eventType === 1;
          const isAccel = seg.eventType === 'AGGREGATION_EVENT_TYPE_ACCELERATION' || seg.eventType === 2;

          // Lighter background for the lines to min and max (whiskers)
          const whiskerFillColor = isBraking
            ? `rgba(239, 68, 68, ${whiskerAlpha})`
            : isAccel
            ? `rgba(34, 197, 94, ${whiskerAlpha})`
            : `rgba(56, 189, 248, ${whiskerAlpha})`;
          // Prominent background for the median range (Q1 to Q3)
          const medianFillColor = isBraking
            ? `rgba(239, 68, 68, ${medianAlpha})`
            : isAccel
            ? `rgba(34, 197, 94, ${medianAlpha})`
            : `rgba(56, 189, 248, ${medianAlpha})`;
          const strokeColor = isBraking
            ? '#ef4444'
            : isAccel
            ? '#22c55e'
            : '#38bdf8';

          const minDistM = seg.minDistanceM ?? (seg.minT * totalLengthM);
          const q1DistM = seg.q1DistanceM ?? (seg.q1T * totalLengthM);
          const medDistM = seg.medianDistanceM ?? (seg.medianT * totalLengthM);
          const q3DistM = seg.q3DistanceM ?? (seg.q3T * totalLengthM);
          const maxDistM = seg.maxDistanceM ?? (seg.maxT * totalLengthM);

          if (seg.sampleCount < 3) continue;

          // 1. Lower whisker background (minDistM -> q1DistM) with lighter background across track width
          drawDistanceBoxPolygon(minDistM, q1DistM, finishDistM, totalLengthM, splineSamples, project, ctx, whiskerFillColor);

          // 2. Upper whisker background (q3DistM -> maxDistM) with lighter background across track width
          drawDistanceBoxPolygon(q3DistM, maxDistM, finishDistM, totalLengthM, splineSamples, project, ctx, whiskerFillColor);

          if (!showHeatmap) {
            // 3. Lower whisker centerline: minDistM -> q1DistM
            drawDistanceCenterlineArc(minDistM, q1DistM, finishDistM, totalLengthM, splineSamples, project, ctx, strokeColor, 2);

            // 4. Upper whisker centerline: q3DistM -> maxDistM
            drawDistanceCenterlineArc(q3DistM, maxDistM, finishDistM, totalLengthM, splineSamples, project, ctx, strokeColor, 2);
          }

          // 5. Whisker end caps across track width
          drawDistancePerpendicularTick(minDistM, finishDistM, totalLengthM, splineSamples, project, ctx, strokeColor, 1.5);
          drawDistancePerpendicularTick(maxDistM, finishDistM, totalLengthM, splineSamples, project, ctx, strokeColor, 1.5);

          // 6. Box polygon from Q1 to Q3 (median range) across track width
          drawDistanceBoxPolygon(q1DistM, q3DistM, finishDistM, totalLengthM, splineSamples, project, ctx, medianFillColor, strokeColor, 1.2);

          // 7. Median crossbar across track width
          drawDistancePerpendicularTick(medDistM, finishDistM, totalLengthM, splineSamples, project, ctx, '#ffffff', 2.0);
        }
      }

      // 2. GPS Driving Line Spatial Density Heat Map (drawn on top of event zones for unobstructed gradient visibility)
      if (showHeatmap && analysisResult.laps && analysisResult.laps.length > 0) {
        drawGpsLineHeatmap(
          ctx,
          analysisResult.laps,
          anchor,
          toCanvas,
          viewport.scale,
          width,
          height
        );
      }

      // 3. Selected Lap Highlight (Full GPS Trajectory + Event Ticks)
      if (selectedLapKey) {
        const selectedLap = analysisResult.laps.find(l => `${l.sessionId}-${l.lapNumber}` === selectedLapKey);
        if (selectedLap) {
          if (selectedLap.gpsPoints && selectedLap.gpsPoints.length >= 2) {
            drawSelectedLapGpsLine(ctx, selectedLap.gpsPoints, anchor, toCanvas);
          }

          if (hasShape && splineSamples.length > 0) {
            const drawSelectedLapTicks = (points: AggregationPoint[], color: string) => {
              for (const pt of points) {
                const closest = findClosestParametricT(
                  { lat: pt.latitudeRaw / 1e6, lng: pt.longitudeRaw / 1e6 },
                  splineSamples
                );
                const sampleWidthM = splineSamples[closest.index]?.widthM || trackWidthM;
                const tickHalfWidthM = (sampleWidthM / 2.0) * 1.15;
                const p1 = getOffsetPoint(closest, tickHalfWidthM);
                const p2 = getOffsetPoint(closest, -tickHalfWidthM);
                const c1 = project(Math.round(p1.lat * 1e6), Math.round(p1.lng * 1e6));
                const c2 = project(Math.round(p2.lat * 1e6), Math.round(p2.lng * 1e6));

                ctx.beginPath();
                ctx.moveTo(c1.x, c1.y);
                ctx.lineTo(c2.x, c2.y);
                ctx.strokeStyle = color;
                ctx.lineWidth = 4;
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(c1.x, c1.y);
                ctx.lineTo(c2.x, c2.y);
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 1.8;
                ctx.stroke();
              }
            };

            drawSelectedLapTicks(selectedLap.brakingPoints || [], '#ef4444');
            drawSelectedLapTicks(selectedLap.accelerationPoints || [], '#22c55e');
            drawSelectedLapTicks(selectedLap.upshiftPoints || [], '#38bdf8');
          } else {
            const drawFallbackEventDots = (points: AggregationPoint[], color: string) => {
              for (const pt of points) {
                const cp = project(pt.latitudeRaw, pt.longitudeRaw);
                ctx.beginPath();
                ctx.arc(cp.x, cp.y, 4.5, 0, Math.PI * 2);
                ctx.fillStyle = color;
                ctx.fill();
                ctx.beginPath();
                ctx.arc(cp.x, cp.y, 1.8, 0, Math.PI * 2);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
              }
            };
            drawFallbackEventDots(selectedLap.brakingPoints || [], '#ef4444');
            drawFallbackEventDots(selectedLap.accelerationPoints || [], '#22c55e');
            drawFallbackEventDots(selectedLap.upshiftPoints || [], '#38bdf8');
          }
        }
      }
    },
    [analysisResult, selectedLapKey, trackShape, trackAnnotations, showHeatmap, showZones]
  );

  // Fetch available tracks
  useEffect(() => {
    let cancelled = false;
    rpc<ListAnalysisTracksResponse>('ListAnalysisTracks', {}).then(resp => {
      if (!cancelled) {
        setAvailableTracks(resp.tracks || []);
      }
    }).catch(console.error);
    return () => { cancelled = true; };
  }, []);

  // Fetch sessions for selected track
  useEffect(() => {
    if (!selectedTrack) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const listResp = await rpc<ListSessionsResponse>('ListSessions', { pageSize: 100, track_name: selectedTrack });
        const filteredSessions = listResp.sessions || [];
        if (filteredSessions.length === 0) {
          setError(`No sessions found for track "${selectedTrack}"`);
          setLoading(false);
          return;
        }

        const detail = await rpc<SessionDetailResponse>('GetSession', { sessionId: filteredSessions[0].id });
        const tId = detail.session.trackId;

        try {
          const shapeResp = await rpc<any>('GetTrackShapeAndAnnotations', { trackId: tId });
          if (shapeResp && !cancelled) {
            setTrackShape(shapeResp.shape || null);
            setTrackAnnotations(shapeResp.annotations || []);
          }
        } catch (err) {
          console.warn('Track shape not available for aggregation analysis, using fallback:', err);
        }

        if (cancelled) return;

        setSessions(filteredSessions);
        setTrackId(tId);

        if (selectedIdsArr.length === 0) {
          const targetSession = initialSessionId
            ? filteredSessions.find(s => s.id === initialSessionId)
            : null;

          const defaultSession = targetSession || filteredSessions.reduce((latest, s) => {
            const sdt = (s.date || '') + ' ' + (s.time || '');
            const ldt = (latest.date || '') + ' ' + (latest.time || '');
            return sdt > ldt ? s : latest;
          }, filteredSessions[0]);

          updateWizardState({
            selectedIds: [defaultSession.id],
            driverFilter: defaultSession.driverName || 'all'
          });
        }
      } catch (e: any) {
        if (!cancelled) {
          const nf = e instanceof RpcError && e.isNotFound;
          setNotFound(nf);
          setError(nf ? 'The requested resource could not be found.' : (e.message || 'Failed to load data'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTrack]);

  const handleAnalyze = useCallback(async (options?: { skipSave?: boolean }) => {
    if (selectedIds.size === 0 || !trackId) return;
    setAnalyzing(true);
    try {
      const resp = await rpc<AnalyzeResponse>('Analyze', {
        skipSave: options?.skipSave,
        aggregation: {
          track: { trackId, trackName: selectedTrack },
          sessions: { sessionIds: [...selectedIds] },
        },
      });
      if (resp.aggregation) {
        setAnalysisResult(resp.aggregation);
        setSelectedLapKey('');
        setStep(2);
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setAnalyzing(false);
    }
  }, [trackId, selectedTrack, selectedIds, setSelectedLapKey, setStep]);

  const handleStepClick = useCallback((targetStep: number) => {
    if (targetStep < step && selectedTrack) {
      setStep(targetStep);
    }
  }, [step, selectedTrack, setStep]);

  // Handle deep-linking into step 2 (e.g. on page refresh or direct URL navigation)
  useEffect(() => {
    if (step === 2 && !analysisResult && selectedIds.size > 0 && !analyzing && trackId) {
      handleAnalyze({ skipSave: true });
    }
  }, [step, analysisResult, selectedIds.size, analyzing, handleAnalyze, trackId]);

  const hasShape = !!(trackShape && trackShape.centerline && trackShape.centerline.length >= 3);

  const handleHover = useCallback((canvasPt: { x: number; y: number } | null, metricPt: { x: number; y: number } | null) => {
    if (!showZones || !canvasPt || !metricPt || !analysisResult?.segments || !trackShape?.centerline?.length) {
      setHoveredSegment(null);
      return;
    }
    const splineSamples = sampleClosedSpline(trackShape.centerline, trackShape.trackWidthM || 8.0, 30);
    const loopClosingDist = splineSamples.length > 0
      ? haversineDistanceM(splineSamples[splineSamples.length - 1].point, splineSamples[0].point)
      : 0;
    const totalLengthM = splineSamples.length > 0
      ? splineSamples[splineSamples.length - 1].distanceM + loopClosingDist
      : (trackShape.totalLengthM || 1000.0);

    const finishLineAnnot = (trackAnnotations || []).find(a => a.type === 'finish_line');
    let finishDistM = 0.0;
    if (finishLineAnnot?.location && splineSamples.length > 0) {
      const closest = findClosestParametricT(finishLineAnnot.location, splineSamples);
      finishDistM = splineSamples[closest.index]?.distanceM ?? 0.0;
    }

    const anchorCoord: GeoCoord = { lat: trackShape.centerline[0].lat, lng: trackShape.centerline[0].lng };
    let closestSeg: AggregationSegment | null = null;
    let closestDist = Infinity;

    for (const seg of analysisResult.segments) {
      if (seg.sampleCount < 3) continue;
      const medDistM = seg.medianDistanceM ?? (seg.medianT * totalLengthM);
      const absD = ((finishDistM + medDistM) % totalLengthM + totalLengthM) % totalLengthM;
      const s = getSplineSampleAtDistanceM(absD, splineSamples, totalLengthM);
      const segMetric = projectToLocalMeters(s.point, anchorCoord);
      const dx = segMetric.x - metricPt.x;
      const dy = segMetric.y - metricPt.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 15 && dist < closestDist) {
        closestDist = dist;
        closestSeg = seg;
      }
    }

    if (closestSeg) {
      setHoveredSegment({
        segment: closestSeg,
        x: canvasPt.x,
        y: canvasPt.y,
      });
    } else {
      setHoveredSegment(null);
    }
  }, [showZones, analysisResult?.segments, trackShape, trackAnnotations]);

  const uniqueDrivers = [...new Set(sessions.map(s => s.driverName || '').filter(Boolean))].sort();

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <ToolHeader
        title={t('analysis.aggregation.title')}
        icon={Layers}
        actions={
          selectedTrack && selectedIds.size > 0 ? (
            <button
              onClick={() => {
                const qp = new URLSearchParams();
                qp.set('session', [...selectedIds].join(','));
                navigate(`#/analysis/aggregation-diff/${encodeURIComponent(selectedTrack)}?${qp.toString()}`);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-lg bg-accent/15 border border-accent/30 hover:bg-accent/25 text-accent transition-all text-xs font-medium cursor-pointer shadow-xs"
              title={t('lapTable.compare')}
            >
              <GitCompareArrows size={14} />
              <span>{t('lapTable.compare')}</span>
            </button>
          ) : undefined
        }
      />

      {/* Wizard stepper */}
      <div className="flex justify-center mb-6">
        <WizardStepper
          steps={wizardSteps}
          currentStep={step}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step 0: Choose Track */}
      {step === 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-border-subtle">
              <h2 className="text-sm font-semibold text-text-primary">{t('gearshifting.wizardChooseTrack')}</h2>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {availableTracks.length > 0 ? (
                availableTracks.map(track => {
                  const isSelected = draftTrack === track;
                  return (
                    <div
                      key={track}
                      onClick={() => setDraftTrack(track)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-border-subtle last:border-0 group ${
                        isSelected ? 'bg-accent/10 border-l-2 border-l-accent' : 'hover:bg-bg-hover'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setDraftTrack(track)}
                          className="w-3.5 h-3.5 accent-accent cursor-pointer"
                        />
                        <span className="text-sm font-medium text-text-primary">{track}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-text-muted text-sm">
                  {t('corner.noTracksWithCorners')}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border-subtle flex justify-end">
              <button
                disabled={!draftTrack}
                onClick={() => {
                  if (draftTrack) {
                    const isNewTrack = draftTrack !== selectedTrack;
                    setSelectedTrack(draftTrack);
                    navigate(`#/analysis/aggregation/${draftTrack}`, true);
                    if (isNewTrack) {
                      setAnalysisResult(null);
                      updateWizardState({
                        step: 1,
                        selectedIds: [],
                        selectedLapKey: '',
                      }, false);
                    } else {
                      setStep(1);
                    }
                  }
                }}
                className={`
                  px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                  ${draftTrack ? 'bg-accent text-white hover:bg-accent-hover' : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'}
                `}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Select Sessions */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto flex flex-col gap-4">
          <div className="card p-3.5 flex items-center gap-3 bg-accent/5 border border-accent/20">
            <Info className="w-4 h-4 text-accent shrink-0" />
            <p className="text-xs text-text-secondary leading-relaxed">
              {t('aggregation.sessionSelectionHint')}
            </p>
          </div>

          <SessionSelector
            sessions={sessions}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            driverFilter={driverFilter}
            onDriverFilterChange={setDriverFilter}
            weatherFilter={weatherFilter}
            onWeatherFilterChange={setWeatherFilter}
            uniqueDrivers={uniqueDrivers}
          />

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>

            <button
              disabled={analyzing || selectedIds.size === 0}
              onClick={() => handleAnalyze()}
              className={`
                flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                ${selectedIds.size > 0 && !analyzing ? 'bg-accent text-white hover:bg-accent-hover' : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'}
              `}
            >
              {analyzing ? <LoadingSpinner size="sm" /> : <Layers size={14} />}
              <span>{analyzing ? t('gearshifting.analyzingLap') : t('analysis.aggregation.title')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Results */}
      {step === 2 && (
        <div className="flex flex-col gap-6">
          {analyzing ? (
            <LoadingSpinner />
          ) : totalLaps === 0 ? (
            <div className="card p-12 flex flex-col items-center justify-center gap-3 text-center">
              <span className="text-3xl opacity-60">📊</span>
              <p className="text-sm text-text-secondary">{t('aggregation.noData')}</p>
              <p className="text-xs text-text-muted">{t('aggregation.noDataHint')}</p>
            </div>
          ) : (
            <>
              {/* Missing track shape notice */}
              {!hasShape && showZones && (
                <div className="card p-6 flex flex-col items-center justify-center gap-3 text-center border-amber-500/30 bg-amber-500/5 max-w-lg mx-auto">
                  <AlertTriangle className="w-8 h-8 text-amber-500" />
                  <h4 className="text-sm font-semibold text-text-primary">{t('aggregation.trackShapeRequired')}</h4>
                  <p className="text-xs text-text-muted max-w-md">{t('aggregation.trackShapeRequiredHint')}</p>
                </div>
              )}

              {/* Satellite Map */}
              {analysisResult?.referenceTrack && analysisResult.referenceTrack.length > 0 && (
                <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-3">
                  <div className="relative aspect-square w-full rounded-2xl shadow-2xl border border-white/10 overflow-hidden mx-auto">
                    <InteractiveTrackMap
                      trackShape={trackShape}
                      trackAnnotations={trackAnnotations}
                      points={analysisResult.referenceTrack.map(s => ({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }))}
                      renderOverlay={renderOverlay}
                      onHover={handleHover}
                      drawRibbonFill={false}
                      className="w-full h-full"
                    >
                      {/* Top-Right Layer Toggles */}
                      <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
                        <button
                          type="button"
                          onClick={() => setShowHeatmap(!showHeatmap)}
                          className={`backdrop-blur-md border text-[10px] font-mono px-2.5 py-1 rounded-full transition-colors shadow-lg cursor-pointer flex items-center gap-1.5 ${
                            showHeatmap
                              ? 'bg-orange-500/20 border-orange-400/50 text-orange-200 hover:bg-orange-500/30'
                              : 'bg-bg-base/80 border-white/10 text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                          }`}
                          aria-pressed={showHeatmap}
                        >
                          <Flame className={`h-3 w-3 ${showHeatmap ? 'text-orange-400' : 'text-text-muted'}`} />
                          <span>{t('aggregation.gpsHeatmap')}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setShowZones(!showZones)}
                          className={`backdrop-blur-md border text-[10px] font-mono px-2.5 py-1 rounded-full transition-colors shadow-lg cursor-pointer flex items-center gap-1.5 ${
                            showZones
                              ? 'bg-accent/25 border-accent/50 text-text-primary hover:bg-accent/35'
                              : 'bg-bg-base/80 border-white/10 text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                          }`}
                          aria-pressed={showZones}
                        >
                          <Layers className={`h-3 w-3 ${showZones ? 'text-accent' : 'text-text-muted'}`} />
                          <span>{t('aggregation.eventZones')}</span>
                        </button>
                      </div>

                      {/* Hover Tooltip */}
                      {hoveredSegment && showZones && (
                        <div
                          className="absolute pointer-events-none z-[600] bg-bg-surface/95 backdrop-blur-sm border border-border-subtle rounded-lg p-2.5 shadow-xl text-xs flex flex-col gap-1 -translate-x-1/2 -translate-y-full -mt-3 transition-all"
                          style={{ left: hoveredSegment.x, top: hoveredSegment.y }}
                        >
                          <div className="flex items-center gap-2 font-semibold">
                            <span
                              className="w-2.5 h-2.5 rounded-xs"
                              style={{
                                backgroundColor:
                                  hoveredSegment.segment.eventType === 'AGGREGATION_EVENT_TYPE_BRAKING' || hoveredSegment.segment.eventType === 1
                                    ? '#ef4444'
                                    : hoveredSegment.segment.eventType === 'AGGREGATION_EVENT_TYPE_ACCELERATION' || hoveredSegment.segment.eventType === 2
                                    ? '#22c55e'
                                    : '#38bdf8',
                              }}
                            />
                            <span className="text-text-primary">
                              {hoveredSegment.segment.eventType === 'AGGREGATION_EVENT_TYPE_BRAKING' || hoveredSegment.segment.eventType === 1
                                ? t('aggregation.brakingSegment')
                                : hoveredSegment.segment.eventType === 'AGGREGATION_EVENT_TYPE_ACCELERATION' || hoveredSegment.segment.eventType === 2
                                ? t('aggregation.accelerationSegment')
                                : t('aggregation.upshiftSegment')}
                            </span>
                            <span className="text-[10px] text-text-muted px-1.5 py-0.5 rounded bg-bg-hover">
                              {t('aggregation.tooltipSamples', { count: hoveredSegment.segment.sampleCount })}
                            </span>
                          </div>
                          <div className="text-[11px] text-text-secondary flex items-center justify-between gap-4">
                            <span className="text-text-muted">{t('aggregation.tooltipMedian')}:</span>
                            <span className="mono font-medium">{hoveredSegment.segment.medianDistanceM.toFixed(1)} m</span>
                          </div>
                          <div className="text-[11px] text-text-secondary flex items-center justify-between gap-4">
                            <span className="text-text-muted">{t('aggregation.tooltipSpread')}:</span>
                            <span className="mono font-medium">
                              {hoveredSegment.segment.minDistanceM.toFixed(1)} m – {hoveredSegment.segment.maxDistanceM.toFixed(1)} m
                            </span>
                          </div>
                        </div>
                      )}
                    </InteractiveTrackMap>
                  </div>

                  {/* Floating Legend below map */}
                  <div className="flex items-center justify-center gap-4 sm:gap-6 py-2 px-5 rounded-full bg-bg-raised/80 backdrop-blur-md border border-border-subtle/80 shadow-lg text-xs mx-auto max-w-fit flex-wrap">
                    {showHeatmap && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-text-muted uppercase tracking-wider">{t('aggregation.gpsHeatmap')}</span>
                        <span className="text-[9px] text-text-muted mono">{t('aggregation.heatmapLow')}</span>
                        <div
                          className="w-16 h-2 rounded-full shadow-inner"
                          style={{
                            background:
                              'linear-gradient(to right, rgba(14, 165, 233, 0.55), #06b6d4, #22c55e, #facc15, #f97316, #ef4444)',
                          }}
                        />
                        <span className="text-[9px] text-text-muted mono">{t('aggregation.heatmapHigh')}</span>
                      </div>
                    )}
                    {showZones && (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-2.5 rounded-xs bg-[#ef4444]/20 border border-[#ef4444]/40 relative flex items-center justify-center overflow-hidden">
                            <div className="w-full h-px bg-[#ef4444]" />
                            <div className="absolute inset-y-0 w-3.5 bg-[#ef4444]/50 border-x border-[#ef4444] flex items-center justify-center">
                              <div className="w-0.5 h-full bg-white" />
                            </div>
                          </div>
                          <span className="text-[10px] text-text-muted uppercase tracking-wider">{t('aggregation.brakingPoints')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-2.5 rounded-xs bg-[#22c55e]/20 border border-[#22c55e]/40 relative flex items-center justify-center overflow-hidden">
                            <div className="w-full h-px bg-[#22c55e]" />
                            <div className="absolute inset-y-0 w-3.5 bg-[#22c55e]/50 border-x border-[#22c55e] flex items-center justify-center">
                              <div className="w-0.5 h-full bg-white" />
                            </div>
                          </div>
                          <span className="text-[10px] text-text-muted uppercase tracking-wider">{t('aggregation.accelerationPoints')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-2.5 rounded-xs bg-[#38bdf8]/20 border border-[#38bdf8]/40 relative flex items-center justify-center overflow-hidden">
                            <div className="w-full h-px bg-[#38bdf8]" />
                            <div className="absolute inset-y-0 w-3.5 bg-[#38bdf8]/50 border-x border-[#38bdf8] flex items-center justify-center">
                              <div className="w-0.5 h-full bg-white" />
                            </div>
                          </div>
                          <span className="text-[10px] text-text-muted uppercase tracking-wider">{t('aggregation.upshiftPoints')}</span>
                        </div>
                      </>
                    )}
                    {selectedLapKey && (
                      <div className="flex items-center gap-2">
                        <div className="w-5 border-t-2 border-dashed border-white drop-shadow-xs" />
                        <span className="text-[10px] text-text-muted uppercase tracking-wider">{t('aggregation.selectedLapLine')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Top 10 Fastest Laps */}
              {top10.length > 0 && (
                <div className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-border-subtle">
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-text-secondary">{t('aggregation.top10Title')}</h3>
                  </div>
                  <div className="max-h-[360px] overflow-y-auto">
                    {sessionGroups.map(group => (
                      <div key={group.sessionId}>
                        {/* Session header */}
                        <div className="px-4 py-2.5 bg-bg-surface border-b border-border-subtle flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-text-secondary">
                            <span>{group.date}</span>
                            <span className="text-text-muted">·</span>
                            <span className="font-medium text-text-primary">{group.driverName}</span>
                          </div>
                        </div>
                        {/* Lap rows */}
                        {group.laps.map(lap => {
                          const lapKey = `${lap.sessionId}-${lap.lapNumber}`;
                          const isSelected = lapKey === selectedLapKey;
                          return (
                            <button
                              key={lapKey}
                              onClick={() => setSelectedLapKey(isSelected ? '' : lapKey)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer border-b border-border-subtle last:border-b-0 ${
                                isSelected
                                  ? 'bg-accent/10 border-l-2 border-l-accent'
                                  : 'hover:bg-bg-hover border-l-2 border-l-transparent'
                              }`}
                            >
                              <span className="text-[0.65rem] font-bold text-text-muted w-5">#{lap.rank}</span>
                              <span className="mono text-xs text-text-secondary w-8">L{lap.lapNumber}</span>
                              <span className="mono text-xs font-semibold text-text-primary flex-1">{formatTime(lap.lapTimeMs)}</span>
                              {lap.rank === 1 && !isSelected && (
                                <span className="text-[0.6rem] font-bold uppercase tracking-wider text-accent bg-accent/10 rounded px-1.5 py-0.5">{t('lapSelector.best')}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {sessionRemovedAnalysis && (
        <SessionRemovedAnalysisDialog
          open={!!sessionRemovedAnalysis}
          onClose={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
          analysis={sessionRemovedAnalysis}
          onDeleted={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
        />
      )}
    </div>
  );
}
